import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import CategoriesManager from "@/components/CategoriesManager";
import { SettingsAPI, SeedAPI } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import SiteHeader from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Save, Settings as SettingsIcon, Database, Trash2, Users } from "lucide-react";
import { SPORTS, SPORT_IDS } from "@/lib/sports";

const TOGGLES = [
  {
    key: "show_recent_medals",
    title: "Carrousel « Dernières médailles gravées »",
    description:
      "Affiche un carrousel horizontal des derniers joueurs créés (tous sports) sur la page d'accueil, entre le hero et le roster.",
  },
  {
    key: "show_sport_tabs",
    title: "Barre d'onglets sport",
    description:
      "Affiche les onglets de filtres par sport (Tous / Football / Tennis / …) sous le header sur les listes joueurs et tournois.",
  },
  {
    key: "show_features_grid",
    title: "Grille des features (QR / NFC / Vidéos / Stats)",
    description:
      "Les 4 cartes glassmorphiques sous le hero principal de la page d'accueil.",
  },
  {
    key: "show_stats_counter",
    title: "Compteur de stats globales",
    description:
      "Bandeau « Joueurs / Équipes / Buts cumulés » en bas du hero d'accueil.",
  },
  {
    key: "show_player_videos",
    title: "Vidéos sur les pages joueurs",
    description:
      "Affiche la section « Highlights » (YouTube / Vimeo / fichiers vidéo) sur chaque page joueur publique.",
  },
  {
    key: "show_tournament_videos",
    title: "Vidéos sur les pages tournois",
    description:
      "Affiche la section « Vidéos de l'évènement » sur chaque page tournoi publique.",
  },
];

export default function AdminSettings() {
  const { refresh } = useSettings();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    SettingsAPI.get()
      .then((d) => setForm(d))
      .catch(() => toast.error("Impossible de charger les paramètres"))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (k) => setForm((f) => ({ ...f, [k]: !f[k] }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const next = await SettingsAPI.update(form);
      setForm(next);
      await refresh();
      toast.success("Paramètres enregistrés");
    } catch {
      toast.error("Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const seedFull = async () => {
    if (!window.confirm(
      "Charger les tournois et joueurs fictifs (3 tournois × ~5 joueurs par sport) ? L'opération est idempotente : les éléments déjà présents ne seront pas dupliqués.",
    )) return;
    setSeeding(true);
    try {
      const r = await SeedAPI.full();
      toast.success(
        `${r.created_tournaments} tournois et ${r.created_players} joueurs créés (${r.skipped_tournaments + r.skipped_players} déjà présents)`,
        { duration: 5000 },
      );
    } catch {
      toast.error("Échec du chargement de la démo complète");
    } finally {
      setSeeding(false);
    }
  };

  const wipeDemo = async () => {
    if (!window.confirm(
      "Supprimer TOUS les tournois et joueurs de démo ? Tes vrais clients (créés à la main) ne seront PAS touchés. Cette action est irréversible.",
    )) return;
    setSeeding(true);
    try {
      const r = await SeedAPI.wipe();
      toast.success(
        `${r.deleted_tournaments} tournois et ${r.deleted_players} joueurs de démo supprimés`,
        { duration: 5000 },
      );
    } catch {
      toast.error("Échec de la suppression de la démo");
    } finally {
      setSeeding(false);
    }
  };

  if (loading || !form)
    return (
      <div className="min-h-screen bg-[#050505] text-zinc-500 font-body flex items-center justify-center">
        Chargement...
      </div>
    );

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SiteHeader />
      <div className="max-w-3xl mx-auto w-full px-4 md:px-8 py-10 md:py-16">
        <div className="flex items-center justify-between gap-4 mb-6">
          <Link
            to="/admin"
            data-testid="settings-back-link"
            className="inline-flex items-center gap-2 text-zinc-500 hover:text-white transition-colors text-xs uppercase tracking-[0.25em] font-body"
          >
            <ArrowLeft className="w-3 h-3" /> Retour
          </Link>
          <div className="flex flex-wrap justify-end gap-2">
            <Link
              to="/admin/associations"
              data-testid="link-admin-associations"
              className="inline-flex items-center gap-2 text-zinc-300 hover:text-white border border-white/15 bg-white/[0.04] rounded-full px-3 py-1.5 text-xs uppercase tracking-[0.18em] font-bold"
            >
              <Users className="w-3 h-3" /> Associations
            </Link>
            <Link
              to="/admin/companies"
              data-testid="link-admin-companies"
              className="inline-flex items-center gap-2 text-zinc-300 hover:text-white border border-white/15 bg-white/[0.04] rounded-full px-3 py-1.5 text-xs uppercase tracking-[0.18em] font-bold"
            >
              <Users className="w-3 h-3" /> Entreprises
            </Link>
            <Link
              to="/admin/leads"
              data-testid="link-admin-leads"
              className="inline-flex items-center gap-2 text-zinc-300 hover:text-white border border-white/15 bg-white/[0.04] rounded-full px-3 py-1.5 text-xs uppercase tracking-[0.18em] font-bold"
            >
              <Users className="w-3 h-3" /> Devis
            </Link>
            <Link
              to="/admin/coaches"
              data-testid="link-admin-coaches"
              className="inline-flex items-center gap-2 text-zinc-300 hover:text-white border border-white/15 bg-white/[0.04] rounded-full px-3 py-1.5 text-xs uppercase tracking-[0.18em] font-bold"
            >
              <Users className="w-3 h-3" /> Coachs
            </Link>
            <Link
              to="/admin/organizers"
              data-testid="link-admin-organizers"
              className="inline-flex items-center gap-2 text-zinc-300 hover:text-white border border-white/15 bg-white/[0.04] rounded-full px-3 py-1.5 text-xs uppercase tracking-[0.18em] font-bold"
            >
              <Users className="w-3 h-3" /> Organisateurs
            </Link>
            <Link
              to="/admin/showcase"
              data-testid="link-admin-showcase"
              className="inline-flex items-center gap-2 text-zinc-300 hover:text-white border border-white/15 bg-white/[0.04] rounded-full px-3 py-1.5 text-xs uppercase tracking-[0.18em] font-bold"
            >
              <SettingsIcon className="w-3 h-3" /> Page Découvrir
            </Link>
          </div>
        </div>

        <div className="flex items-center gap-4 mb-3">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{
              background: "rgba(212,175,55,0.12)",
              border: "1px solid rgba(212,175,55,0.3)",
            }}
          >
            <SettingsIcon className="w-5 h-5" style={{ color: "#D4AF37" }} />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: "#D4AF37" }}>
              Admin · Configuration
            </div>
            <h1 className="font-display text-3xl md:text-5xl font-black uppercase tracking-tight leading-none">
              Paramètres du site
            </h1>
          </div>
        </div>

        <p className="text-zinc-400 font-body mb-10 max-w-xl">
          Active ou désactive les sections de la page publique. Tes choix
          s'appliquent immédiatement pour tous les visiteurs.
        </p>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="space-y-3"
          data-testid="settings-form"
        >
          {TOGGLES.map((t) => (
            <div
              key={t.key}
              data-testid={`setting-row-${t.key}`}
              className="metal-card rounded-xl p-5 flex items-start gap-4"
            >
              <Switch
                id={t.key}
                data-testid={`setting-toggle-${t.key}`}
                checked={!!form[t.key]}
                onCheckedChange={() => toggle(t.key)}
                className="mt-1 data-[state=checked]:bg-white data-[state=unchecked]:bg-white/10"
              />
              <div className="flex-1 min-w-0">
                <Label
                  htmlFor={t.key}
                  className="font-display text-lg font-bold uppercase tracking-tight cursor-pointer"
                >
                  {t.title}
                </Label>
                <p className="text-zinc-400 font-body text-sm mt-1 leading-relaxed">
                  {t.description}
                </p>
                <div
                  className={`inline-flex items-center gap-1.5 mt-3 px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-[0.25em] font-bold border ${
                    form[t.key]
                      ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/10"
                      : "border-zinc-700 text-zinc-500 bg-zinc-900/40"
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      form[t.key] ? "bg-emerald-400" : "bg-zinc-600"
                    }`}
                  />
                  {form[t.key] ? "Activé" : "Désactivé"}
                </div>
              </div>
            </div>
          ))}

          <div className="pt-4 flex justify-end">
            <Button
              type="submit"
              disabled={saving}
              data-testid="save-settings"
              className="text-black rounded-md px-6 h-11 font-body font-medium hover:opacity-90"
              style={{
                background:
                  "linear-gradient(180deg, #FFE9A8 0%, #D4AF37 60%, #B8941F 100%)",
              }}
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Enregistrement..." : "Enregistrer les paramètres"}
            </Button>
          </div>
        </motion.form>

        {/* Sport visibility toggles */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="mt-12 metal-card rounded-xl p-5"
          data-testid="sports-visibility-card"
        >
          <div className="flex items-start gap-4 mb-5">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-white/[0.06] border border-white/15">
              <SettingsIcon className="w-4 h-4 text-zinc-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg font-bold uppercase tracking-tight">
                Sports affichés
              </div>
              <p className="text-zinc-400 font-body text-sm mt-1 leading-relaxed">
                Coche les sports que tu veux voir apparaître publiquement
                (onglets de filtres, listes joueurs, listes tournois). Décoche
                pour cacher complètement un sport sans supprimer ses données.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {SPORT_IDS.map((sid) => {
              const sport = SPORTS[sid];
              const enabled =
                !form.enabled_sports || form.enabled_sports.includes(sid);
              return (
                <button
                  key={sid}
                  type="button"
                  onClick={() => {
                    const current = form.enabled_sports || SPORT_IDS;
                    const next = enabled
                      ? current.filter((x) => x !== sid)
                      : [...current, sid];
                    setForm({ ...form, enabled_sports: next });
                  }}
                  data-testid={`sport-toggle-${sid}`}
                  className="relative flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-all text-left"
                  style={{
                    background: enabled ? "rgba(255,255,255,0.06)" : "transparent",
                    borderColor: enabled ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.08)",
                    opacity: enabled ? 1 : 0.45,
                  }}
                >
                  <div
                    className="w-5 h-5 rounded flex items-center justify-center shrink-0"
                    style={{
                      background: enabled ? "#FFFFFF" : "transparent",
                      border: `1.5px solid ${enabled ? "#FFFFFF" : "#52525b"}`,
                    }}
                  >
                    {enabled && (
                      <svg
                        className="w-3 h-3 text-black"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path
                          d="M2 6.5L4.5 9L10 3"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  <span className="font-display text-xs font-bold uppercase tracking-tight truncate">
                    {sport.label}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-600 mt-4 font-body">
            N'oublie pas de cliquer « Enregistrer les paramètres » plus haut
            pour sauvegarder.
          </p>
        </motion.div>

        {/* Demo data seed */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.15 }}
          className="mt-12 metal-card rounded-xl p-5 flex items-start gap-4"
          data-testid="seed-demo-card"
        >
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{
              background: "rgba(212,175,55,0.12)",
              border: "1px solid rgba(212,175,55,0.3)",
            }}
          >
            <Database className="w-4 h-4" style={{ color: "#D4AF37" }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg font-bold uppercase tracking-tight">
              Charger la démo complète
            </div>
            <p className="text-zinc-400 font-body text-sm mt-1 leading-relaxed">
              Crée automatiquement 3 tournois fictifs par sport (~33 au total) et
              ~70 joueurs fictifs avec stats, équipes et photos sport-spécifiques.
              Idempotent : ne duplique rien si déjà présent.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                type="button"
                onClick={seedFull}
                disabled={seeding}
                data-testid="seed-full-demo-btn"
                className="h-10 px-5 rounded-md text-black font-bold uppercase tracking-[0.18em] text-xs hover:opacity-90"
                style={{
                  background:
                    "linear-gradient(180deg, #FFE9A8 0%, #D4AF37 60%, #B8941F 100%)",
                }}
              >
                <Database className="w-3.5 h-3.5 mr-2" />
                {seeding ? "..." : "Charger la démo"}
              </Button>
              <Button
                type="button"
                onClick={wipeDemo}
                disabled={seeding}
                variant="outline"
                data-testid="wipe-demo-btn"
                className="h-10 px-5 rounded-md font-bold uppercase tracking-[0.18em] text-xs border-red-500/40 bg-red-500/5 text-red-400 hover:bg-red-500/15 hover:text-red-300"
              >
                <Trash2 className="w-3.5 h-3.5 mr-2" />
                Supprimer la démo
              </Button>
            </div>
          </div>
        </motion.div>

        <div className="mt-8">
          <CategoriesManager />
        </div>
      </div>
    </div>
  );
}
